import React, { useCallback, useEffect, useState } from "react";
import { Loader, Modal } from "@mantine/core";
import schedulerService from "../../../services/scheduler";
import ShiftDrawer from "../Scheduler/ShiftDrawer";
import Icon from "../Scheduler/icons";
import { addDays, isoDate, sameDay, startOfDay, DOW, MON_SHORT, MONTHS } from "../Scheduler/scheduleUtils";
import { decrypt } from "../../../utils";
import "./CommandCenterCalendar.css";

// Client photos / facility logos are stored encrypted (some legacy rows are plain URLs).
const safeImg = (v) => { if (!v) return null; try { return decrypt(v) || v; } catch { return v; } };

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const initials = (s) => (s || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";
const hm = (m) => { const mm = (((m % 1440) + 1440) % 1440); const h = Math.floor(mm / 60); const min = mm % 60; const h12 = h % 12 === 0 ? 12 : h % 12; return `${h12}:${String(min).padStart(2, "0")}`; };
const mer = (m) => (Math.floor((((m % 1440) + 1440) % 1440) / 60) < 12 ? "AM" : "PM");

// Map an appointment to display fields. Client appts → who = client, Facility appts → who = facility.
const apptInfo = (a) => {
  const isClient = a.type !== "facility";
  const who = isClient ? (a.clientName || a.client?.name || "Client") : (a.facility?.name || "Facility");
  return {
    isClient,
    who,
    img: isClient ? safeImg(a.client?.imageurl) : safeImg(a.facility?.photo),
    title: a.apptTitle || (isClient ? "Client appointment" : "Facility appointment"),
    sub: isClient ? `Client · ${who}` : `Facility · ${who}`,
    tone: isClient ? "info" : "teal",
    dot: isClient ? "#2E90FA" : "#0F766E",
  };
};

// Command Center "Calendar" tab — mini month calendar + Today / Upcoming lists,
// across all facilities' appointments. Modeled on the Appointments Widget design.
export default function CommandCenterCalendar({ listOnly = false }) {
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const now = new Date();
    // Cover the displayed month AND today→+45d so the calendar dots and the
    // Today/Upcoming lists are all populated from one fetch.
    const lo = new Date(Math.min(startOfMonth(monthDate).getTime(), startOfDay(now).getTime()));
    const hi = new Date(Math.max(endOfMonth(monthDate).getTime(), addDays(now, 45).getTime()));
    const data = await schedulerService.getAppointments({ kind: "appointment", from: isoDate(lo), to: isoDate(hi) });
    setItems(
      (data || [])
        .filter((a) => a && a.date)
        .sort((a, b) => new Date(a.date) - new Date(b.date) || (a.startMin || 0) - (b.startMin || 0))
    );
    setLoading(false);
  }, [monthDate]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (s) => {
    // Virtual open shifts (generated from schedules) have no DB doc to fetch.
    if (s?.virtual) {
      setDetail(s);
      return;
    }
    const full = await schedulerService.getShift(idOf(s));
    setDetail(full || s);
  };

  const renderTodayRow = (a) => {
    const info = apptInfo(a);
    return (
      <div key={idOf(a)} className="cca-appt" onClick={() => openDetail(a)}>
        <span className={`cca-avatar cca-tone-${info.tone} ${info.isClient ? "" : "cca-avatar-sq"}`}>
          {initials(info.who)}
          {info.img ? <img src={info.img} alt="" className="cca-avatar-img" onError={(e) => e.currentTarget.remove()} /> : null}
        </span>
        <div className="cca-appt-body">
          <div className="cca-appt-title">{info.title}</div>
          <div className="cca-appt-meta">
            <span className={`cca-chip cca-chip-${info.isClient ? "client" : "facility"}`}>{info.isClient ? "Client" : "Facility"}</span>
            <span className="cca-appt-who">{info.who}</span>
          </div>
        </div>
        <div className="cca-when">
          {a.allDay ? <span className="cca-allday">All day</span> : (<><span className="cca-when-s">{hm(a.startMin)} {mer(a.startMin)}</span><span className="cca-when-e">{hm(a.endMin)} {mer(a.endMin)}</span></>)}
        </div>
      </div>
    );
  };

  const renderUpRow = (a) => {
    const info = apptInfo(a);
    const d = new Date(a.date);
    return (
      <div key={idOf(a)} className="cca-up" onClick={() => openDetail(a)}>
        <div className="cca-up-date"><span className="cca-up-wd">{DOW[d.getDay()].toUpperCase()}</span><span className="cca-up-d">{d.getDate()}</span></div>
        <div className="cca-appt-body">
          <div className="cca-appt-title">{info.title}</div>
          <div className="cca-appt-meta">
            <span className={`cca-chip cca-chip-${info.isClient ? "client" : "facility"}`}>{info.isClient ? "Client" : "Facility"}</span>
            <span className="cca-appt-who">{info.who}</span>
          </div>
        </div>
        <div className="cca-when">
          {a.allDay ? <span className="cca-allday">All day</span> : (<><span className="cca-when-s">{hm(a.startMin)} {mer(a.startMin)}</span><span className="cca-when-e">{hm(a.endMin)} {mer(a.endMin)}</span></>)}
        </div>
      </div>
    );
  };

  const now = new Date();
  const todays = items.filter((a) => sameDay(new Date(a.date), now));
  const futureAll = items.filter((a) => startOfDay(new Date(a.date)) > startOfDay(now));

  // Cap inline rows so the panel never overflows (the calendar must never clip).
  // Today is prioritized; upcoming fills any remaining slots; the rest open in a modal.
  // 4 rows fit exactly; when there's overflow we drop to 3 to leave room for the "+more" button.
  const totalCount = todays.length + futureAll.length;
  // List-only mode lives in a scrollable panel, so it can show more rows before
  // collapsing the remainder into the "+more" modal.
  const cap = listOnly ? 8 : totalCount > 4 ? 3 : 4;
  const todaysShown = todays.slice(0, cap);
  const upcoming = futureAll.slice(0, Math.max(0, cap - todaysShown.length));
  const moreCount = totalCount - (todaysShown.length + upcoming.length);

  // month grid (Sunday-first) — only the weeks this month spans, no 6th-week padding
  const mStart = startOfMonth(monthDate);
  const gridStart = addDays(mStart, -mStart.getDay());
  const daysInMonth = endOfMonth(monthDate).getDate();
  const totalCells = Math.ceil((mStart.getDay() + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => addDays(gridStart, i));
  const apptOn = (d) => items.find((a) => sameDay(new Date(a.date), d));
  const isCurrentMonth = monthDate.getMonth() === now.getMonth() && monthDate.getFullYear() === now.getFullYear();

  return (
    <div className={`cca-root${listOnly ? " cca-list-only" : ""}`}>
      <div className="cca-cols">
        {/* mini month calendar */}
        {!listOnly ? (
        <div className="cca-cal-card">
          <div className="cca-cal-hdr">
            <button type="button" className="cca-navbtn" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} aria-label="Previous month"><Icon name="chevronLeft" size={15} /></button>
            <span className="cca-cal-month">{MONTHS[monthDate.getMonth()]} {monthDate.getFullYear()}</span>
            <button type="button" className="cca-navbtn" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} aria-label="Next month"><Icon name="chevron" size={15} /></button>
          </div>
          {!isCurrentMonth ? (
            <div className="cca-center" style={{ marginBottom: 10 }}>
              <button type="button" className="cca-today-btn" onClick={() => setMonthDate(startOfMonth(new Date()))}>Jump to today</button>
            </div>
          ) : null}
          <div className="cca-cal-grid">
            {["S", "M", "T", "W", "T", "F", "S"].map((w, i) => <div key={i} className="cca-cal-wd">{w}</div>)}
          </div>
          <div className="cca-cal-cells">
            {cells.map((d, i) => {
              const inMonth = d.getMonth() === monthDate.getMonth();
              if (!inMonth) return <div key={i} className="cca-cal-cell" />;
              const isToday = sameDay(d, now);
              const a = apptOn(d);
              const cls = isToday ? "today" : startOfDay(d) < startOfDay(now) ? "past" : "future";
              return (
                <div key={i} className="cca-cal-cell">
                  <div className={`cca-cal-num ${cls}`}>{d.getDate()}</div>
                  {a ? <span className="cca-cal-dot" style={{ background: apptInfo(a).dot }} /> : null}
                </div>
              );
            })}
          </div>
        </div>
        ) : null}

        {!listOnly ? <div className="cca-divider" /> : null}

        {/* Today + Upcoming lists — calendar stays fixed, this column scrolls */}
        <div className="cca-lists">
          <div className="cca-section">
            <div className="cca-band cca-band-today">
              <div className="cca-band-head">
                <span className="cca-sec-title">Today</span>
                <span className="cca-sec-dot">·</span>
                <span className="cca-sec-sub">{DOW[now.getDay()]}, {MON_SHORT[now.getMonth()]} {now.getDate()}</span>
              </div>
              <div className="cca-sec-count">{todays.length} {todays.length === 1 ? "appt" : "appts"}</div>
            </div>
            {loading ? (
              <div className="cca-empty"><Loader size="xs" color="var(--app-primary)" /></div>
            ) : todays.length === 0 ? (
              <div className="cca-empty">No appointments today.</div>
            ) : (
              <div className="cca-list">{todaysShown.map(renderTodayRow)}</div>
            )}
          </div>

          <div className="cca-section">
            <div className="cca-band cca-band-up">
              <div className="cca-sec-title">Upcoming</div>
            </div>
            {upcoming.length > 0 ? (
              <div className="cca-list">{upcoming.map(renderUpRow)}</div>
            ) : (
              <div className="cca-empty">No upcoming scheduled appointments.</div>
            )}
          </div>

          {moreCount > 0 ? (
            <button type="button" className="cca-more-btn" onClick={() => setShowAll(true)}>+ {moreCount} more</button>
          ) : null}
        </div>
      </div>

      <Modal
        opened={showAll}
        onClose={() => setShowAll(false)}
        title="All appointments"
        centered
        radius={14}
        size={480}
        zIndex={1000}
        overlayProps={{ opacity: 0.45, blur: 1 }}
        styles={{ title: { fontWeight: 700, color: "var(--app-text)" }, body: { paddingTop: 6 } }}
      >
        <div className="cca-modal-body">
          {todays.length > 0 ? (
            <div className="cca-section">
              <div className="cca-band cca-band-today">
                <div>
                  <div className="cca-sec-title">Today</div>
                  <div className="cca-sec-sub">{DOW[now.getDay()]}, {MON_SHORT[now.getMonth()]} {now.getDate()}</div>
                </div>
                <div className="cca-sec-count">{todays.length} {todays.length === 1 ? "appt" : "appts"}</div>
              </div>
              <div className="cca-list">{todays.map(renderTodayRow)}</div>
            </div>
          ) : null}
          {futureAll.length > 0 ? (
            <div className="cca-section">
              <div className="cca-band cca-band-up">
                <div className="cca-sec-title">Upcoming</div>
              </div>
              <div className="cca-list">{futureAll.map(renderUpRow)}</div>
            </div>
          ) : null}
        </div>
      </Modal>

      <ShiftDrawer opened={!!detail} shift={detail} onClose={() => setDetail(null)} readOnly />
    </div>
  );
}
